import React, { useState } from 'react'
import classNames from 'classnames'

import { Select } from '@zambezi/sdk/dropdown-list'
import { IsViewport } from '@zambezi/layout-grid'
import TabBar from '@zambezi/sdk/tab-bar'

import PaymentDetails from '@apiEnabler/ob-common/lib/view/stores/PaymentDetails'
import DateTimeWarning from '@apiEnabler/ob-common/lib/view/DateTimeWarning'
import {
  vrpDescriptiveDate,
  getHistoryForCurrentUser,
  getClientAndMerchantText,
} from '@apiEnabler/ob-common/lib/vrp-consents'
import {
  formatDate,
  durationRemaining,
} from '@apiEnabler/ob-common/lib/date-utils'

import ConsentOverviewHeader from './ConsentOverviewHeader'
import NavBar, { NavItem } from './NavBar'
import PaymentHistoryEntries from './PaymentHistoryEntries'

import style from './VrpPayment.module.scss'

export default function VrpPayment({
  consent,
  brand,
  tpp,
  onCancelAccess,
  isStaffMember,
}) {
  const allHistoryTypes = ['Payment', 'Connection']
  const { label, date } = vrpDescriptiveDate(consent)
  const handleCancel = createCancelHandler(consent, onCancelAccess)
  const [historyType, setHistoryType] = useState(allHistoryTypes[0])
  const historyEntries =
    historyType === 'Payment'
      ? consent.get('activities')
      : getHistoryForCurrentUser(consent, isStaffMember)
  const selectedHistoryTabIndex = allHistoryTypes.indexOf(historyType)
  const clientAndMerchantText = getClientAndMerchantText(consent, tpp)

  return (
    <section className="js-access-container">
      <header className={style.header}>
        <div className={style.headerContent}>
          <NavBar
            items={[
              new NavItem({
                label: 'Variable recurring payments',
                link: '/recurring-payments/vrp/tpps',
              }),
              new NavItem({
                label: clientAndMerchantText,
                link: `/recurring-payments/vrp/tpps/${tpp.clientId}/payments`,
              }),
            ]}
            padded
          />
          <ConsentOverviewHeader
            dateLabel={label}
            dateValue={date}
            description={<VrpDescriptiveMessage consent={consent} tpp={tpp} />}
            onCancel={handleCancel}
            cancelLabel="Cancel payment permission"
            consentStatus={consent.get('status')}
          />
        </div>
      </header>

      <section className={style.content}>
        <PaymentDetails payment={consent} brand={brand} />
        <h2
          id="history-title"
          className={`${style.sectionHeading} js-history-title`}
        >
          History
        </h2>

        <IsViewport isWidescreen isDesktop>
          <TabBar
            className={classNames(style.historyTabs, 'js-history-tabs')}
            data={allHistoryTypes}
            selectedIndex={selectedHistoryTabIndex}
            onSelected={historyType => {
              setHistoryType(historyType)
            }}
          />

          <PaymentHistoryEntries
            historyType={historyType}
            historyEntries={historyEntries}
          />
        </IsViewport>

        <IsViewport isTablet isMobile>
          <Select
            className={classNames(style.historyTypeSelector, 'js-history-tabs')}
            defaultValue={historyType}
            suggestions={allHistoryTypes}
            onSuggestionSelected={(e, { suggestion }) => {
              setHistoryType(suggestion)
            }}
          />

          <PaymentHistoryEntries
            historyType={historyType}
            historyEntries={historyEntries}
            isMobile
          />
        </IsViewport>

        <DateTimeWarning />
      </section>
    </section>
  )
}

function createCancelHandler(access, onCancelAccess) {
  if (onCancelAccess && access.get('grouping') === 'ACTIVE') {
    return () => {
      onCancelAccess(access)
    }
  }
  return null
}

export function VrpDescriptiveMessage({ consent, tpp }) {
  const createdDate = (
    <span className={style.descriptionDate}>
      {formatDate(consent.get('createdAt'), 'Do MMMM YYYY')}
    </span>
  )
  const clientAndMerchantText = getClientAndMerchantText(consent, tpp)
  const clientName = (
    <span className={style.descriptionTpp}>{clientAndMerchantText}</span>
  )

  const accessRemainingTime =
    consent.has('expiresAt') && durationRemaining(consent.get('expiresAt'))

  switch (consent.grouping) {
    case 'DELETED':
      return (
        <p className={style.description}>
          Payment permission is no longer given to {clientName} as they
          requested to delete your connection.
        </p>
      )
    case 'WITHDRAWN':
      return (
        <p className={style.description}>
          Payment permission is no longer given to {clientName}. You or a member
          of staff has chosen to cancel your agreement.
        </p>
      )
    case 'BLOCKED':
      return (
        <p className={style.description}>
          This status is not displayed in the customer access store. Payment
          access has been blocked. This state occurs where there could
          potentially be harmful to the Bank and customer and is therefore
          denied.
        </p>
      )
    case 'DECLINED':
      return (
        <p className={style.description}>
          This status is not displayed in the customer access store. Payment
          access has been declined. The authorisation process has been
          cancelled.
        </p>
      )
    case 'EXPIRED':
      return (
        <p className={style.description}>
          Payment permission is no longer given to {clientName}. The permission
          you gave on {createdDate} has expired.
        </p>
      )
    case 'TPP_REVOKED':
      return (
        <p className={style.description}>
          Payment permission is no longer given to {clientName} as it's been
          revoked. Please contact them for more information.
        </p>
      )
    case 'TPP_SUSPENDED':
    case 'TPP_CLIENT_SUSPENDED':
      return (
        <p className={style.description}>
          Payment permission is no longer given to {clientName}. Please contact
          them for more information.
        </p>
      )
    default:
      return (
        <p className={style.description}>
          On {createdDate}, you gave payment permission to {clientName}.{' '}
          {accessRemainingTime &&
            `The permission expires in ${accessRemainingTime}.`}
        </p>
      )
  }
}
